import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Church, Lock, User } from "lucide-react";

import GlobalLoadingPage from "../../../core/components/GlobalLoadingPage";
import ChurchLogo from "../../../assets/logos/Church-Logo.png";
import LoginBackground from "../../../assets/backgrounds/login screen Background.png";
import { churchSupabase } from "../services/churchSupabaseClient";
import {
  clearChurchOrgUserSession,
  getStoredChurchOrgUserSession,
  loginChurchOrgUser,
  lookupChurchOrgByDomain,
  lookupChurchOrg,
  requestChurchOrgUserJoin,
} from "../services/churchOrgUserAuthService";
import { loadGlobalStyles } from "../services/globalAdminService";

const MIN_LOADING_MS = 1200;
const LOGIN_DEBUG_PREFIX = "[ChurchLogin]";
const CHURCH_ORG_CACHE_KEY = "oikos.organization.church";
const CHURCH_PENDING_REDIRECT_KEY = "oikos.church.pendingRedirect";

function shouldLogLoginDebug() {
  if (typeof window === "undefined") return process.env.NODE_ENV !== "production";

  return (
    process.env.NODE_ENV !== "production" ||
    window.localStorage.getItem("oikos.church.debugAuth") === "true"
  );
}

function loginDebug(message, details = {}) {
  if (!shouldLogLoginDebug()) return;
  console.info(LOGIN_DEBUG_PREFIX, message, details);
}

function describeOrgSession(session) {
  return {
    hasSession: Boolean(session?.sessionToken),
    accountId: String(session?.account?.id || "").slice(0, 8),
    orgUserId: String(session?.orgUser?.id || "").slice(0, 8),
    expiresAt: session?.expiresAt || "",
  };
}

function wait(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

function getPathFromLocation(nextLocation, fallback = "/church") {
  if (!nextLocation?.pathname) return fallback;

  return `${nextLocation.pathname}${nextLocation.search || ""}${nextLocation.hash || ""}`;
}

function isSafeRedirectPath(redirectPath = "") {
  return (
    redirectPath &&
    redirectPath.startsWith("/") &&
    !redirectPath.startsWith("//") &&
    redirectPath !== "/church/login" &&
    !redirectPath.startsWith("/church/login/")
  );
}

function getRedirectFromSearch(search = "") {
  try {
    const params = new URLSearchParams(search || "");
    const redirectPath = params.get("redirect") || params.get("next") || "";

    return isSafeRedirectPath(redirectPath) ? redirectPath : "";
  } catch (_error) {
    return "";
  }
}

function getStoredPendingRedirect() {
  if (typeof window === "undefined") return "";

  try {
    const redirectPath = window.sessionStorage.getItem(CHURCH_PENDING_REDIRECT_KEY) || "";
    return isSafeRedirectPath(redirectPath) ? redirectPath : "";
  } catch (_error) {
    return "";
  }
}

function clearStoredPendingRedirect() {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.removeItem(CHURCH_PENDING_REDIRECT_KEY);
  } catch (_error) {
    // Ignore private browsing or storage permission failures.
  }
}

function buildLoginSearch(currentSearch = "", redirectPath = "") {
  const params = new URLSearchParams(currentSearch || "");
  const cleanRedirect = getRedirectFromSearch(`?redirect=${encodeURIComponent(redirectPath)}`);

  if (cleanRedirect && !params.get("redirect")) {
    params.set("redirect", cleanRedirect);
  }

  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : "";
}

function getOrgFromSearch(search = "") {
  try {
    const params = new URLSearchParams(search || "");
    return (
      params.get("org") ||
      params.get("accountId") ||
      params.get("account") ||
      params.get("churchId") ||
      params.get("church") ||
      ""
    );
  } catch (_error) {
    return "";
  }
}

function cacheChurchOrganization(organization) {
  if (typeof window === "undefined" || !organization?.id) return;

  try {
    const rawExisting = window.localStorage.getItem(CHURCH_ORG_CACHE_KEY);
    const existing = rawExisting ? JSON.parse(rawExisting) : null;
    const nextOrganization = {
      ...existing,
      ...organization,
      logo_url:
        organization.logo_url ||
        organization.logoUrl ||
        existing?.logo_url ||
        existing?.logoUrl ||
        "",
      logo_path: organization.logo_path || existing?.logo_path || "",
    };

    window.localStorage.setItem(CHURCH_ORG_CACHE_KEY, JSON.stringify(nextOrganization));
    window.dispatchEvent(
      new CustomEvent("oikos-theme-change", {
        detail: {
          mode: "church",
          accountId: nextOrganization.id,
        },
      })
    );
  } catch (_error) {
    // Ignore local install metadata cache failures.
  }
}

export default function ChurchLoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { orgId = "" } = useParams();
  const stateFrom = location.state?.from;
  const redirectFromSearch = getRedirectFromSearch(location.search);
  const storedRedirect = getStoredPendingRedirect();
  const orgLookupCode =
    String(orgId || "").trim() ||
    getOrgFromSearch(location.search) ||
    getOrgFromSearch(stateFrom?.search);
  const [orgCode, setOrgCode] = useState(orgLookupCode);
  const [organization, setOrganization] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(Boolean(orgLookupCode));
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinDisplayName, setJoinDisplayName] = useState("");
  const [joinUsername, setJoinUsername] = useState("");
  const [joinPassword, setJoinPassword] = useState("");
  const [joinEmail, setJoinEmail] = useState("");
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinError, setJoinError] = useState("");
  const [joinNotice, setJoinNotice] = useState("");
  const [globalStyles, setGlobalStyles] = useState({});

  const fromPath = getPathFromLocation(stateFrom, redirectFromSearch || storedRedirect || "/church");
  const hasOrganization = Boolean(organization?.id);
  const organizationLoginCode =
    organization?.church_code || organization?.invite_code || orgCode || orgLookupCode;
  const loginLogo = organization?.logo_url || ChurchLogo;
  const loadingTitle = hasOrganization ? "Signing In" : "Finding Church";
  const loadingDetail = hasOrganization
    ? `Opening ${organization?.name || "your church"}...`
    : "Finding your organization login...";
  const loginBackgroundImage = globalStyles?.loginBackgroundUrl || LoginBackground;

  useEffect(() => {
    let isMounted = true;

    loadGlobalStyles()
      .then((nextStyles) => {
        if (isMounted) setGlobalStyles(nextStyles || {});
      })
      .catch((stylesError) => {
        console.warn(LOGIN_DEBUG_PREFIX, "global styles load failed", stylesError);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const cleanOrgId = String(orgLookupCode || "").trim();
    if (!cleanOrgId) return undefined;

    loginDebug("route org lookup started", {
      orgIdLength: cleanOrgId.length,
      fromPath,
    });
    setOrgCode(cleanOrgId);
    setBusy(true);
    setError("");

    lookupChurchOrg(cleanOrgId)
      .then((nextOrganization) => {
        if (!isMounted) return;

        if (!nextOrganization?.id) {
          loginDebug("route org lookup returned no organization");
          setError("We could not find that church.");
          return;
        }

        loginDebug("route org lookup succeeded", {
          accountId: String(nextOrganization.id || "").slice(0, 8),
          name: nextOrganization.name,
          churchCode: nextOrganization.church_code || "",
        });
        cacheChurchOrganization(nextOrganization);
        setOrganization(nextOrganization);
      })
      .catch((orgError) => {
        if (isMounted) {
          console.error(LOGIN_DEBUG_PREFIX, "route org lookup failed", orgError);
          setError(orgError.message || "We could not find that church.");
        }
      })
      .finally(() => {
        if (isMounted) setBusy(false);
      });

    return () => {
      isMounted = false;
    };
  }, [orgLookupCode]);

  useEffect(() => {
    let isMounted = true;
    if (orgLookupCode || organization?.id || typeof window === "undefined") return undefined;

    const hostname = window.location.hostname;
    if (!hostname || hostname === "localhost" || hostname === "127.0.0.1") return undefined;

    setBusy(true);
    setError("");

    lookupChurchOrgByDomain(hostname)
      .then((nextOrganization) => {
        if (!isMounted || !nextOrganization?.id) return;
        cacheChurchOrganization(nextOrganization);
        setOrganization(nextOrganization);
        setOrgCode(nextOrganization.church_code || nextOrganization.invite_code || nextOrganization.id || "");
      })
      .catch((domainError) => {
        if (isMounted) {
          console.warn(LOGIN_DEBUG_PREFIX, "domain org lookup failed", domainError);
        }
      })
      .finally(() => {
        if (isMounted) setBusy(false);
      });

    return () => {
      isMounted = false;
    };
  }, [orgLookupCode, organization?.id]);

  async function handleOrgSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    loginDebug("org code submit started", {
      orgCodeLength: orgCode.trim().length,
      fromPath,
    });

    try {
      const nextOrganization = await lookupChurchOrg(orgCode);

      if (!nextOrganization?.id) {
        loginDebug("org code submit found no organization");
        setError("We could not find that church.");
        setBusy(false);
        return;
      }

      loginDebug("org code submit found organization", {
        accountId: String(nextOrganization.id || "").slice(0, 8),
        name: nextOrganization.name,
      });
      cacheChurchOrganization(nextOrganization);
      setOrganization(nextOrganization);
      setJoinOpen(false);
      setJoinError("");
      setJoinNotice("");
      const nextOrgCode = nextOrganization.id || nextOrganization.church_code || nextOrganization.invite_code || orgCode;
      if (nextOrgCode) {
        loginDebug("navigating to org login route", {
          nextOrgCode,
        });
        navigate(`/church/login/${encodeURIComponent(nextOrgCode)}${buildLoginSearch(location.search, fromPath)}`, {
          replace: true,
          state: location.state,
        });
      }
      setBusy(false);
    } catch (orgError) {
      console.error(LOGIN_DEBUG_PREFIX, "org code submit failed", orgError);
      setBusy(false);
      setError(orgError.message || "We could not find that church.");
    }
  }

  async function handleJoinRequest() {
    if (!organization?.id || joinBusy) return;

    setJoinError("");
    setJoinNotice("");

    if (!joinUsername.trim()) {
      setJoinError("Choose a username for your church login.");
      return;
    }

    if (!joinPassword || joinPassword.length < 6) {
      setJoinError("Choose a password with at least 6 characters.");
      return;
    }

    setJoinBusy(true);

    try {
      await requestChurchOrgUserJoin({
        accountId: organization.id,
        displayName: joinDisplayName,
        username: joinUsername,
        password: joinPassword,
        email: joinEmail,
      });

      setJoinNotice("Request sent. An admin can approve this login in Organization Users.");
      setJoinDisplayName("");
      setJoinUsername("");
      setJoinPassword("");
      setJoinEmail("");
    } catch (joinRequestError) {
      console.error(LOGIN_DEBUG_PREFIX, "join request failed", joinRequestError);
      setJoinError(joinRequestError.message || "Could not send your join request.");
    } finally {
      setJoinBusy(false);
    }
  }

  async function handleSignInSubmit(event) {
    event.preventDefault();
    const loadingStartedAt = Date.now();
    setBusy(true);
    setError("");
    loginDebug("sign-in submit started", {
      accountId: String(organization?.id || "").slice(0, 8),
      username: email.trim(),
      usernameLooksLikeEmail: email.includes("@"),
      fromPath,
    });

    try {
      clearChurchOrgUserSession();
      loginDebug("cleared any previous org session before sign-in");

      if (email.includes("@")) {
        loginDebug("trying Supabase owner email sign-in");
        const { error: signInError } = await churchSupabase.auth.signInWithPassword({
          email,
          password,
        });

        if (!signInError) {
          loginDebug("Supabase owner email sign-in succeeded", { fromPath });
          const elapsed = Date.now() - loadingStartedAt;
          if (elapsed < MIN_LOADING_MS) {
            await wait(MIN_LOADING_MS - elapsed);
          }

          clearStoredPendingRedirect();
          navigate(fromPath, { replace: true });
          return;
        }

        loginDebug("Supabase owner email sign-in failed; trying org user login", {
          code: signInError?.code || "",
          message: signInError?.message || String(signInError),
        });
      }

      await churchSupabase.auth.signOut().catch(() => null);
      loginDebug("Supabase auth signed out before org user login");
      const orgUserSession = await loginChurchOrgUser(organization.id, email, password);
      loginDebug("org user login returned", describeOrgSession(orgUserSession));

      const elapsed = Date.now() - loadingStartedAt;
      if (elapsed < MIN_LOADING_MS) {
        await wait(MIN_LOADING_MS - elapsed);
      }

      if (!orgUserSession) {
        throw new Error("Could not sign in with that username and password.");
      }

      loginDebug("stored org session after login", describeOrgSession(getStoredChurchOrgUserSession()));
      loginDebug("navigating after org user login", { fromPath });
      clearStoredPendingRedirect();
      navigate(fromPath, { replace: true });
    } catch (signInError) {
      console.error(LOGIN_DEBUG_PREFIX, "sign-in submit failed", signInError);
      setBusy(false);
      setError(signInError.message || "Could not sign in.");
    }
  }

  return (
    <main style={{ ...styles.page, backgroundImage: `linear-gradient(90deg, rgba(9, 12, 16, 0.28), rgba(9, 12, 16, 0.06)), url("${loginBackgroundImage}")` }}>
      {busy ? (
        <GlobalLoadingPage
          title={loadingTitle}
          detail={loadingDetail}
          modeOverride="church"
          backgroundImageOverride={loginBackgroundImage}
        />
      ) : null}
      <form onSubmit={hasOrganization ? handleSignInSubmit : handleOrgSubmit} style={styles.card}>
        <img src={ChurchLogo} alt="Oikos Church" style={styles.logo} />
        {!hasOrganization ? (
          <>
            <label style={styles.inputWrap}>
              <Church size={18} style={styles.inputIcon} />
              <input
                autoComplete="organization"
                disabled={busy}
                onChange={(event) => setOrgCode(event.target.value)}
                placeholder="Church code"
                required
                style={styles.input}
                type="text"
                value={orgCode}
              />
            </label>
            {error ? <div style={styles.error}>{error}</div> : null}
            <button disabled={busy} style={styles.button} type="submit">
              {busy ? "Checking..." : "Continue"}
            </button>
          </>
        ) : (
          <>
            <button
              disabled={busy || joinBusy}
              onClick={() => {
                setOrganization(null);
                setPassword("");
                setError("");
                setJoinOpen(false);
                setJoinError("");
                setJoinNotice("");
                navigate(`/church/login${buildLoginSearch(location.search, fromPath)}`, {
                  replace: true,
                  state: location.state,
                });
              }}
              style={styles.orgIdentityButton}
              title="Change church"
              type="button"
            >
              <span style={styles.orgLogoFrame}>
                <img src={loginLogo} alt="" style={styles.orgLogoImage} />
              </span>
              <span style={styles.orgText}>
                <span style={styles.orgLabel}>Signing in to</span>
                <span style={styles.orgName}>{organization.name || "Church Login"}</span>
                <span style={styles.orgCode}>{organizationLoginCode}</span>
              </span>
            </button>
            <label style={styles.inputWrap}>
              <User size={18} style={styles.inputIcon} />
              <input
                autoComplete="username"
                disabled={busy}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Username or owner email"
                required
                style={styles.input}
                type="text"
                value={email}
              />
            </label>
            <label style={styles.inputWrap}>
              <Lock size={18} style={styles.inputIcon} />
              <input
                autoComplete="current-password"
                disabled={busy}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Password"
                required
                style={styles.input}
                type="password"
                value={password}
              />
            </label>
            {error ? <div style={styles.error}>{error}</div> : null}
            <button disabled={busy} style={styles.button} type="submit">
              {busy ? "Signing in..." : "Sign In"}
            </button>
            <button
              disabled={busy || joinBusy}
              onClick={() => {
                setJoinOpen((current) => !current);
                setJoinError("");
                setJoinNotice("");
                setJoinUsername((current) => current || email);
              }}
              style={styles.joinToggleButton}
              type="button"
            >
              Join "{organization.name || "Church"}"
            </button>
            {joinOpen ? (
              <div style={styles.joinPanel}>
                <label style={styles.inputWrap}>
                  <User size={18} style={styles.inputIcon} />
                  <input
                    autoComplete="name"
                    disabled={busy || joinBusy}
                    onChange={(event) => setJoinDisplayName(event.target.value)}
                    placeholder="Full name"
                    style={styles.input}
                    type="text"
                    value={joinDisplayName}
                  />
                </label>
                <label style={styles.inputWrap}>
                  <User size={18} style={styles.inputIcon} />
                  <input
                    autoComplete="username"
                    disabled={busy || joinBusy}
                    onChange={(event) => setJoinUsername(event.target.value)}
                    placeholder="Requested username"
                    style={styles.input}
                    type="text"
                    value={joinUsername}
                  />
                </label>
                <label style={styles.inputWrap}>
                  <Lock size={18} style={styles.inputIcon} />
                  <input
                    autoComplete="new-password"
                    disabled={busy || joinBusy}
                    onChange={(event) => setJoinPassword(event.target.value)}
                    placeholder="Requested password"
                    style={styles.input}
                    type="password"
                    value={joinPassword}
                  />
                </label>
                <label style={styles.inputWrap}>
                  <User size={18} style={styles.inputIcon} />
                  <input
                    autoComplete="email"
                    disabled={busy || joinBusy}
                    onChange={(event) => setJoinEmail(event.target.value)}
                    placeholder="Email, optional"
                    style={styles.input}
                    type="email"
                    value={joinEmail}
                  />
                </label>
                {joinError ? <div style={styles.error}>{joinError}</div> : null}
                {joinNotice ? <div style={styles.joinNotice}>{joinNotice}</div> : null}
                <button
                  disabled={busy || joinBusy}
                  onClick={handleJoinRequest}
                  style={styles.joinRequestButton}
                  type="button"
                >
                  {joinBusy ? "Sending..." : "Request Login"}
                </button>
              </div>
            ) : null}
          </>
        )}
      </form>
    </main>
  );
}

const styles = {
  page: {
    alignItems: "center",
    backgroundColor: "#101418",
    backgroundImage: `linear-gradient(90deg, rgba(9, 12, 16, 0.28), rgba(9, 12, 16, 0.06)), url("${LoginBackground}")`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    boxSizing: "border-box",
    display: "grid",
    inset: 0,
    minHeight: "100dvh",
    overflow: "auto",
    padding: 24,
    position: "fixed",
    width: "100vw",
  },
  card: {
    background: "transparent",
    display: "grid",
    gap: 12,
    margin: "0 auto",
    maxWidth: 360,
    padding: "20px 10px",
    position: "relative",
    width: "100%",
    zIndex: 2,
  },
  logo: {
    height: "auto",
    justifySelf: "center",
    marginBottom: 8,
    objectFit: "contain",
    width: "100%",
  },
  inputWrap: {
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.86)",
    border: "1px solid rgba(255, 255, 255, 0.92)",
    borderRadius: 999,
    boxSizing: "border-box",
    boxShadow: "0 10px 24px rgba(28, 49, 40, 0.13)",
    display: "flex",
    gap: 10,
    minHeight: 48,
    padding: "0 16px",
    width: "100%",
  },
  inputIcon: {
    color: "#6f8067",
    flex: "0 0 auto",
  },
  input: {
    background: "transparent",
    border: 0,
    boxSizing: "border-box",
    color: "#18202a",
    flex: 1,
    font: "500 15px inherit",
    minWidth: 0,
    outline: "none",
    padding: "13px 0",
    width: "100%",
  },
  error: {
    color: "#b91c1c",
    fontSize: 14,
    lineHeight: 1.4,
  },
  orgIdentityButton: {
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.76)",
    border: "1px solid rgba(255, 255, 255, 0.9)",
    borderRadius: 14,
    boxShadow: "0 16px 34px rgba(28, 49, 40, 0.16)",
    boxSizing: "border-box",
    color: "#1e2a1d",
    cursor: "pointer",
    display: "flex",
    font: "inherit",
    gap: 14,
    minHeight: 92,
    padding: 14,
    textAlign: "left",
    width: "100%",
  },
  orgLogoFrame: {
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.84)",
    border: "1px solid rgba(255, 255, 255, 0.95)",
    borderRadius: 12,
    boxShadow: "0 10px 22px rgba(28, 49, 40, 0.12)",
    display: "flex",
    flex: "0 0 64px",
    height: 64,
    justifyContent: "center",
    overflow: "hidden",
    padding: 8,
    width: 64,
  },
  orgLogoImage: {
    display: "block",
    height: "100%",
    objectFit: "contain",
    width: "100%",
  },
  orgText: {
    display: "grid",
    flex: "1 1 auto",
    gap: 3,
    minWidth: 0,
  },
  orgLabel: {
    color: "#65735a",
    display: "block",
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.1,
    textTransform: "uppercase",
  },
  orgName: {
    color: "#202a1d",
    display: "block",
    fontSize: 19,
    fontWeight: 800,
    lineHeight: 1.15,
    overflowWrap: "anywhere",
  },
  orgCode: {
    color: "#65735a",
    display: "block",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.25,
    overflowWrap: "anywhere",
  },
  button: {
    background: "#5f7d4d",
    border: 0,
    borderRadius: 999,
    boxShadow: "0 12px 24px rgba(95, 125, 77, 0.24)",
    color: "#ffffff",
    cursor: "pointer",
    font: "inherit",
    fontWeight: 700,
    marginTop: 2,
    minHeight: 48,
    padding: "0 15px",
  },
  joinToggleButton: {
    background: "rgba(255, 255, 255, 0.8)",
    border: "1px solid rgba(255, 255, 255, 0.92)",
    borderRadius: 999,
    boxShadow: "0 10px 20px rgba(28, 49, 40, 0.12)",
    color: "#263321",
    cursor: "pointer",
    font: "inherit",
    fontSize: 14,
    fontWeight: 800,
    minHeight: 44,
    padding: "0 15px",
  },
  joinPanel: {
    background: "rgba(255, 255, 255, 0.68)",
    border: "1px solid rgba(255, 255, 255, 0.86)",
    borderRadius: 18,
    boxShadow: "0 18px 38px rgba(28, 49, 40, 0.14)",
    display: "grid",
    gap: 10,
    padding: 12,
  },
  joinRequestButton: {
    background: "#263321",
    border: 0,
    borderRadius: 999,
    color: "#ffffff",
    cursor: "pointer",
    font: "inherit",
    fontSize: 14,
    fontWeight: 800,
    minHeight: 44,
    padding: "0 15px",
  },
  joinNotice: {
    color: "#166534",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.4,
  },
};
